import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { reviewModeOf, type ReviewMode } from "../lib/reviewWrite";

/* THE COACH'S MODE — `profiles.review_mode` on their OWN row (migration `20260926100000`; the owner's SELECT and UPDATE
 * policies, rehearsed arms 19–21). Read once per mount; a change is written, then read back from the server, never kept
 * as a local copy of the value. A failed read is `null` mode + a message, never the default passed off as the coach's. */
export type ModeState = { mode: ReviewMode | null; error: string | null; saving: boolean; set: (m: ReviewMode) => void };

export function useReviewMode(coachId: string): ModeState {
  const [mode, setMode] = useState<ReviewMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const read = useCallback(async () => {
    const r = await supabase.from("profiles").select("review_mode").eq("id", coachId).maybeSingle();
    if (r.error !== null) { setError(r.error.message); return; }
    setError(null);
    setMode(reviewModeOf((r.data as { review_mode: string } | null)?.review_mode));
  }, [coachId]);
  useEffect(() => { void read(); }, [read]);
  const set = useCallback((m: ReviewMode) => {
    setSaving(true);
    void supabase.from("profiles").update({ review_mode: m }).eq("id", coachId).then(async ({ error: e }) => {
      if (e !== null) setError(`That did not save: ${e.message}`);
      await read();
      setSaving(false);
    });
  }, [coachId, read]);
  return { mode, error, saving, set };
}
